<template>
  <div class="semaforo-coincidencias" v-if="evaluacion">
    <div class="cabecera">
      <span class="bola" :class="'bola-' + color"></span>
      <span class="texto">{{ $t('coincidencias.semaforo.' + color) }}</span>
      <span class="detalle">{{ $t('coincidencias.diasLaborables', { n: evaluacion.diasLaborables || 0 }) }}</span>
    </div>

    <!-- El semáforo en palabras (#65). Lo compone el servidor con plantilla, sin IA y sin
         nombres cuando quien pregunta es el propio solicitante, así que se pinta tal cual
         llegue y no hay nada que filtrar aquí. -->
    <p v-if="evaluacion.explicacion" class="explicacion-semaforo">{{ evaluacion.explicacion }}</p>

    <p v-if="!reglas.length" class="sin-conflictos">{{ $t('coincidencias.sinConflictos') }}</p>

    <div v-for="regla in reglas" :key="regla.reglaId" class="regla" :class="'regla-' + regla.semaforo">
      <div class="regla-titulo">
        <span class="bola pequena" :class="'bola-' + regla.semaforo"></span>
        {{ regla.titulo }}
        <span class="etiqueta-severidad" :class="'sev-' + regla.severidad">
          {{ $t('coincidencias.severidades.' + regla.severidad) }}
        </span>
        <span class="minimo">{{ $t('coincidencias.minimoDe', { n: regla.minimoPresentes }) }}</span>
      </div>
      <p v-if="regla.explicacion" class="explicacion">{{ regla.explicacion }}</p>
      <ul class="dias">
        <li v-for="dia in regla.dias" :key="dia.fecha" :class="'dia-' + dia.semaforo">
          <span class="fecha">{{ formatearFecha(dia.fecha) }}</span>
          <!-- Con nombres sólo cuando el servidor los manda: al solicitante no le llegan
               (decisión D-IA), y entonces se dice cuántos son sin decir quiénes. -->
          <span v-if="conNombres">{{ textoConNombres(dia) }}</span>
          <span v-else>{{ textoAnonimo(dia) }}</span>
        </li>
      </ul>
    </div>

    <div v-if="requiereJustificacion" class="aviso-justificacion">
      {{ $t('coincidencias.exigeJustificacion') }}
    </div>

    <div v-if="alternativas.length" class="alternativas">
      <div class="alternativas-titulo">{{ $t('coincidencias.alternativasTitulo') }}</div>
      <ul>
        <li v-for="alternativa in alternativas" :key="alternativa.fechaInicio">
          <a href="#" @click.prevent="$emit('elegir-alternativa', alternativa)">
            {{ formatearFecha(alternativa.fechaInicio) }} – {{ formatearFecha(alternativa.fechaFin) }}
          </a>
          <span class="detalle">
            {{ $t('coincidencias.alternativaDetalle', {
              dias: alternativa.diasLaborables,
              distancia: alternativa.distanciaDias
            }) }}
          </span>
        </li>
      </ul>
    </div>
    <p v-else-if="evaluacion.mensajeAlternativas" class="sin-alternativas">
      {{ evaluacion.mensajeAlternativas }}
    </p>
  </div>
</template>
<style scoped>
.semaforo-coincidencias { font-size: 13px; }
.cabecera { display: flex; align-items: center; gap: 8px; font-weight: bold; margin-bottom: 6px; }
.cabecera .detalle { font-weight: normal; color: #8e8e93; font-size: 12px; }
.bola { display: inline-block; width: 14px; height: 14px; border-radius: 50%; }
.bola.pequena { width: 10px; height: 10px; }
.bola-VERDE { background-color: #4cd964; }
.bola-AMBAR { background-color: #ff9500; }
.bola-ROJO { background-color: #d32f2f; }
.sin-conflictos { color: #8e8e93; margin: 0 0 6px; }
.explicacion-semaforo { margin: 0 0 8px; }
.regla { border-left: 3px solid #e0e0e0; padding: 4px 0 4px 8px; margin-bottom: 8px; }
.regla-AMBAR { border-left-color: #ff9500; }
.regla-ROJO { border-left-color: #d32f2f; }
.regla-titulo { font-weight: bold; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.etiqueta-severidad {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 11px;
  color: #fff;
  background-color: #8e8e93;
}
.sev-BLOQUEANTE { background-color: #22355b; }
.sev-AVISO { background-color: #ff9500; }
.minimo { font-weight: normal; color: #8e8e93; font-size: 12px; }
.explicacion { margin: 2px 0 4px; color: #555; font-style: italic; }
.dias { list-style: none; margin: 0; padding: 0; }
.dias li { padding: 1px 0; }
.dias .fecha { font-weight: bold; margin-right: 6px; }
.dia-ROJO { color: #d32f2f; }
.dia-AMBAR { color: #b26a00; }
.aviso-justificacion {
  background-color: #fdecea;
  border: 1px solid #d32f2f;
  color: #a12020;
  border-radius: 4px;
  padding: 6px 8px;
  margin: 6px 0;
}
.alternativas { margin-top: 8px; }
.alternativas-titulo { font-weight: bold; margin-bottom: 2px; }
.alternativas ul { list-style: none; margin: 0; padding: 0; }
.alternativas li { padding: 1px 0; }
.alternativas .detalle { color: #8e8e93; font-size: 12px; margin-left: 6px; }
.sin-alternativas { color: #8e8e93; margin: 6px 0 0; }
</style>
<script>
/**
 * Semáforo de coincidencias de vacaciones (#64). Pinta el resultado de
 * `POST /rh/coincidencias/evaluar` tal como llega, sin decidir nada por su cuenta.
 *
 * Es el mismo componente para los dos sitios donde se enseña —la ventana de solicitud (#58) y
 * la bandeja del aprobador (#59)— porque la diferencia entre ellos no es de pantalla: es que a
 * un solicitante el servidor no le manda los nombres de los compañeros. Aquí sólo se lee
 * `conNombres` de la respuesta.
 */

/** Quién es cada persona: su usuario, y el nombre sólo si llega sin usuario. */
function usuarioDe(persona) {
  const usuario = persona && persona.username ? String(persona.username).trim() : '';
  return usuario || null;
}

/** Lo que se lee: el nombre de la ficha, y el usuario si la ficha no trae nombre. */
function nombreVisible(persona) {
  const nombre = persona && persona.nombre ? String(persona.nombre).trim() : '';
  return nombre || usuarioDe(persona);
}

function claveDe(persona) {
  const usuario = usuarioDe(persona);
  if (usuario) return usuario.toUpperCase();
  const nombre = nombreVisible(persona);
  return nombre ? 'nombre:' + nombre : null;
}

export default {
  name: 'r-semaforo-coincidencias',
  props: {
    evaluacion: {
      type: Object,
      default: null
    }
  },
  computed: {
    color: function() {
      return (this.evaluacion && this.evaluacion.semaforo) || 'VERDE';
    },
    conNombres: function() {
      return !!(this.evaluacion && this.evaluacion.conNombres);
    },
    reglas: function() {
      return (this.evaluacion && this.evaluacion.reglas) || [];
    },
    alternativas: function() {
      return (this.evaluacion && this.evaluacion.alternativas) || [];
    },
    requiereJustificacion: function() {
      return !!(this.evaluacion && this.evaluacion.requiereJustificacion);
    },
    /**
     * Cómo se nombra a cada compañero, decidido UNA vez sobre toda la evaluación (#65).
     *
     * Dos cuentas distintas pueden llamarse igual —dos «Ana García»—, y pintando cada lista por
     * su cuenta el aprobador lee el mismo nombre en las confirmadas y en las pendientes y cree
     * que es una persona con dos solicitudes. Cuando hay empate la etiqueta lleva el usuario
     * detrás, igual que hace el texto que compone la API (`RhNombresCompaneros`), para que lo
     * que se lee aquí y lo que se lee en la explicación digan lo mismo.
     */
    etiquetasPersonas: function() {
      const personas = {};
      const conEseNombre = {};
      this.reglas.forEach(function(regla) {
        (regla.dias || []).forEach(function(dia) {
          const todos = (dia.companerosConfirmados || []).concat(dia.companerosPendientes || []);
          todos.forEach(function(persona) {
            const clave = claveDe(persona);
            if (!clave || personas[clave]) return;
            const visible = nombreVisible(persona);
            personas[clave] = persona;
            conEseNombre[visible] = (conEseNombre[visible] || 0) + 1;
          });
        });
      });
      const etiquetas = {};
      Object.keys(personas).forEach(function(clave) {
        const persona = personas[clave];
        const visible = nombreVisible(persona);
        const usuario = usuarioDe(persona);
        etiquetas[clave] = conEseNombre[visible] > 1 && usuario && usuario !== visible
          ? visible + ' (' + usuario + ')'
          : visible;
      });
      return etiquetas;
    }
  },
  methods: {
    formatearFecha: function(iso) {
      // Las fechas llegan en ISO (yyyy-mm-dd) y se cortan a mano: construir un Date con esa
      // cadena la interpreta en UTC y en España se pinta el día anterior.
      if (!iso) return '';
      var partes = iso.split('-');
      return partes.length === 3 ? partes[2] + '/' + partes[1] + '/' + partes[0] : iso;
    },
    nombres: function(personas) {
      const etiquetas = this.etiquetasPersonas;
      return (personas || [])
        .map(function(persona) {
          const clave = claveDe(persona);
          return clave && etiquetas[clave] ? etiquetas[clave] : nombreVisible(persona);
        })
        .filter(function(etiqueta) { return !!etiqueta; })
        .join(', ');
    },
    textoConNombres: function(dia) {
      var partes = [];
      if (dia.numeroConfirmados) {
        partes.push(this.$t('coincidencias.confirmadasDe', { quienes: this.nombres(dia.companerosConfirmados) }));
      }
      if (dia.numeroPendientes) {
        partes.push(this.$t('coincidencias.pendientesDe', { quienes: this.nombres(dia.companerosPendientes) }));
      }
      return partes.join(' · ');
    },
    textoAnonimo: function(dia) {
      var partes = [];
      if (dia.numeroConfirmados) {
        partes.push(this.$tc('coincidencias.confirmadasAnonimas', dia.numeroConfirmados,
          { n: dia.numeroConfirmados }));
      }
      if (dia.numeroPendientes) {
        partes.push(this.$tc('coincidencias.pendientesAnonimas', dia.numeroPendientes,
          { n: dia.numeroPendientes }));
      }
      return partes.join(' · ');
    }
  }
};
</script>
